import React, { useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Input, Row, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";

import { MissingDocumentItem, PPPRowHeader } from ".";
import { i18, trb } from "@utilities";
import { ApplicationsActions, selectIsRequestAdditionaDocsLoading } from "@redux";
import { DOCUMENT_FLAG_OPTIONS } from "@config";

const { Option } = Select;

const INITIAL_DOC_INFO_STATE = {
  comment: "",
  type: "",
  documentName: "",
  fileCommentFlags: DOCUMENT_FLAG_OPTIONS.FILE_WITH_MANDATORY_COMMENT
};

const DOCUMENT_CONFIG_OPTIONS = [
  {
    label: i18.ApplicationsLabels.FileWithMandatoryComment,
    value: DOCUMENT_FLAG_OPTIONS.FILE_WITH_MANDATORY_COMMENT
  },
  {
    label: i18.ApplicationsLabels.FileWithoutComment,
    value: DOCUMENT_FLAG_OPTIONS.FILE_WITHOUT_COMMENT
  },
  {
    label: i18.ApplicationsLabels.FileWithOptionalComment,
    value: DOCUMENT_FLAG_OPTIONS.FILE_WITH_OPTIONAL_COMMENT
  },
  {
    label: i18.ApplicationsLabels.OptionalFileMandatoryComment,
    value: DOCUMENT_FLAG_OPTIONS.OPTIONAL_FILE_MANDATORY_COMMENT
  },
  {
    label: i18.ApplicationsLabels.MandatoryComment,
    value: DOCUMENT_FLAG_OPTIONS.MANDATORY_COMMENT
  }
];

const hasValue = value => value && value.toString().trim() !== "";

const validations = [
  {
    field: "type",
    validation: hasValue,
    message: i18.Validations.FieldRequired
  },
  {
    field: "comment",
    validation: hasValue,
    message: i18.Validations.FieldRequired
  },
  {
    field: "documentName",
    validation: hasValue,
    message: i18.Validations.FieldRequired
  }
];

const validate = values =>
  validations.reduce((errors, { field, validation, message }) => {
    if (!validation(values[field])) {
      errors[field] = message;
    }
    return errors;
  }, {});

export const PPPRequestDocs = React.memo(({ appId }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsRequestAdditionaDocsLoading);

  const [missingDocuments, setMissingDocuments] = useState([]);
  const [missingDocInfo, setMissingDocInfo] = useState(INITIAL_DOC_INFO_STATE);
  const [errorFields, setErrorFields] = useState();

  const handleInputChange = e => {
    setMissingDocInfo({
      ...missingDocInfo,
      [e.target.name]: e.target.value
    });
  };

  const addMissingDocument = () => {
    setErrorFields(undefined);
    const errorFields = validate(missingDocInfo);

    if (Object.keys(errorFields).length !== 0) {
      setErrorFields(errorFields);
      return;
    }

    const newMissingDoc = {
      reviewerComment: missingDocInfo.comment,
      documentName: missingDocInfo.documentName,
      documentType: missingDocInfo.type,
      fileCommentFlags: missingDocInfo.fileCommentFlags
    };
    setMissingDocuments([...missingDocuments, newMissingDoc]);
    setMissingDocInfo(INITIAL_DOC_INFO_STATE);
  };

  const handleDocDelete = name => {
    setMissingDocuments([...missingDocuments.filter(doc => doc.documentName !== name)]);
  };

  const submitMissingDocs = async () => {
    try {
      await dispatch(ApplicationsActions.setMissingDocuments(appId, missingDocuments));
    } catch (e) {
      setMissingDocuments([]);
    }
  };

  return (
    <>
      <Divider />
      <PPPRowHeader />
      {missingDocuments?.map((doc, index) => (
        <MissingDocumentItem
          {...doc}
          key={`${doc.documentName}-${index}`}
          handleDocDelete={handleDocDelete}
        />
      ))}

      <Row className="mt-3">
        <Col span={4}>
          <Input
            value={missingDocInfo.documentName}
            onChange={handleInputChange}
            name="documentName"
            className="w-100 mr-2"
            placeholder={trb(i18.ApplicationsLabels.CheckDocumentName)}
          />
          {errorFields?.documentName && (
            <span className="text-error eb_fs_10">{trb(errorFields.documentName)}</span>
          )}
        </Col>
        <Col span={5} offset={1}>
          <Input
            value={missingDocInfo.comment}
            onChange={handleInputChange}
            className="w-100 mr-2"
            name="comment"
            placeholder={trb(i18.ApplicationsLabels.CheckDocumentMissingDocComment)}
          />
          {errorFields?.comment && (
            <span className="text-error eb_fs_10">{trb(errorFields.comment)}</span>
          )}
        </Col>
        <Col span={4} offset={1}>
          <Input
            className="w-100 mr-2"
            name="type"
            value={missingDocInfo.type}
            onChange={handleInputChange}
            placeholder={trb(i18.ApplicationsLabels.CheckDocumentMissingDocType)}
          />
          {errorFields?.type && (
            <span className="text-error eb_fs_10">{trb(errorFields.type)}</span>
          )}
        </Col>
        <Col span={5} offset={1}>
          <Select
            value={missingDocInfo.fileCommentFlags}
            onChange={value =>
              setMissingDocInfo({
                ...missingDocInfo,
                fileCommentFlags: value
              })
            }
            name="fileCommentFlags"
          >
            {DOCUMENT_CONFIG_OPTIONS.map(option => (
              <Option value={option.value} key={option.value}>
                {trb(option.label)}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={2} offset={1}>
          <Button
            type="primary"
            shape="circle"
            icon={<PlusOutlined />}
            onClick={addMissingDocument}
          />
        </Col>
      </Row>

      <Row>
        <Col span={24} className="mt-3 d-flex">
          <Button
            onClick={submitMissingDocs}
            type="primary"
            disabled={isLoading}
            className="mr-2"
            loading={isLoading}
          >
            {`${trb(i18.Buttons.KycDocumentApprove)}`}
          </Button>
        </Col>
      </Row>
    </>
  );
});
